import { ref } from 'vue';
// Lightweight composable for reading commit history from the GitHub REST API.
// It deliberately does NOT use useApi/useAuth — those prepend the Syso backend
// base URL, whereas here we talk to api.github.com directly with our own token.
const REPO = 'tphillips/Syso';
const BRANCH = 'dev';
const TOKEN_KEY = 'gh-history-token';
const PER_PAGE = 100;
const MAX_PAGES = 30; // ~3000 commits per query — guards against runaway requests
export function useGitHistory() {
    const commits = ref([]);
    const loading = ref(false);
    const error = ref(null);
    const truncated = ref(false);
    const authError = ref(false);
    const hasToken = ref(!!localStorage.getItem(TOKEN_KEY));
    function getToken() {
        return localStorage.getItem(TOKEN_KEY);
    }
    function setToken(token) {
        localStorage.setItem(TOKEN_KEY, token.trim());
        hasToken.value = true;
        authError.value = false;
    }
    function clearToken() {
        localStorage.removeItem(TOKEN_KEY);
        hasToken.value = false;
    }
    function mapCommit(raw) {
        const sha = raw.sha;
        const fullMessage = raw.commit?.message ?? '';
        return {
            sha,
            shortSha: sha.substring(0, 7),
            author: raw.commit?.author?.name ?? 'Unknown',
            login: raw.author?.login ?? null,
            avatar: raw.author?.avatar_url ?? null,
            date: raw.commit?.author?.date ?? '',
            message: fullMessage.split('\n')[0]
        };
    }
    // Paginated fetch for an optional sub-path (e.g. 'Sites', 'Services').
    // Sets the shared error/authError/truncated flags; returns the commits it got.
    async function loadCommits(opts, path) {
        const token = getToken();
        if (!token) {
            authError.value = true;
            return [];
        }
        const collected = [];
        for (let page = 1; page <= MAX_PAGES; page++) {
            const params = new URLSearchParams({
                sha: BRANCH,
                since: opts.since,
                until: opts.until,
                per_page: String(PER_PAGE),
                page: String(page)
            });
            if (path)
                params.set('path', path);
            const response = await fetch(`https://api.github.com/repos/${REPO}/commits?${params.toString()}`, {
                headers: {
                    Authorization: `bearer ${token}`,
                    Accept: 'application/vnd.github+json'
                }
            });
            if (response.status === 401) {
                authError.value = true;
                error.value = 'GitHub rejected the token (401). Please enter a valid token.';
                return collected;
            }
            if (!response.ok) {
                error.value = `GitHub API error (${response.status}): ${response.statusText}`;
                return collected;
            }
            const batch = await response.json();
            if (!Array.isArray(batch) || batch.length === 0) {
                break;
            }
            collected.push(...batch.map(mapCommit));
            if (batch.length < PER_PAGE) {
                break; // last page
            }
            if (page === MAX_PAGES) {
                truncated.value = true;
            }
        }
        return collected;
    }
    // Loads the whole repo into shared state for the main views.
    async function fetchCommits(opts) {
        loading.value = true;
        error.value = null;
        truncated.value = false;
        authError.value = false;
        try {
            commits.value = await loadCommits(opts);
        }
        catch (e) {
            error.value = e?.message || 'Failed to reach the GitHub API.';
        }
        finally {
            loading.value = false;
        }
    }
    // Loads commits touching a given sub-path; returns the array (no shared state).
    async function fetchCommitsForPath(opts, path) {
        try {
            return await loadCommits(opts, path);
        }
        catch {
            return [];
        }
    }
    function mapRun(raw) {
        const sha = raw.head_sha ?? '';
        return {
            id: raw.id,
            name: raw.name ?? 'Workflow',
            status: raw.status ?? 'unknown',
            conclusion: raw.conclusion ?? null,
            headSha: sha,
            headShaShort: sha.substring(0, 7),
            event: raw.event ?? '',
            htmlUrl: raw.html_url ?? '',
            actor: raw.actor?.login ?? raw.triggering_actor?.login ?? null,
            createdAt: raw.created_at ?? '',
            updatedAt: raw.updated_at ?? ''
        };
    }
    // Latest workflow runs on BRANCH in a single request. Each run carries its
    // head_sha, so the page can both list runs and decorate commits without any
    // per-commit calls. Returns [] on auth/error (flags are not disturbed here —
    // the commit fetch owns the shared error state).
    async function fetchWorkflowRuns(perPage = 50) {
        const token = getToken();
        if (!token)
            return [];
        const params = new URLSearchParams({
            branch: BRANCH,
            per_page: String(perPage)
        });
        try {
            const response = await fetch(`https://api.github.com/repos/${REPO}/actions/runs?${params.toString()}`, {
                headers: {
                    Authorization: `bearer ${token}`,
                    Accept: 'application/vnd.github+json'
                }
            });
            if (!response.ok)
                return [];
            const data = await response.json();
            const runs = Array.isArray(data?.workflow_runs) ? data.workflow_runs : [];
            return runs.map(mapRun);
        }
        catch {
            return [];
        }
    }
    return {
        commits,
        loading,
        error,
        truncated,
        authError,
        hasToken,
        getToken,
        setToken,
        clearToken,
        fetchCommits,
        fetchCommitsForPath,
        fetchWorkflowRuns,
        REPO,
        BRANCH
    };
}
